import { iMerchant } from "core/kangarooTypes/iMerhant";
import { iAddresses } from "core/kangarooTypes/ProfileManagement";
import {
  addAddress,
  deleteAddress,
  setAddressAsPrimary,
  updateAddress,
} from "core/Services/MerchantServices";
import { Edit, Loader, MapPin, Plus, Save, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "shared/Button";
import MapLocationWidget from "shared/MapLocationWidget";
import { USER_ID } from "utils/constant";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

interface Coordinates {
  lat: number;
  lng: number;
}

const INITIAL_FORM_VALUES: iAddresses = {
  id: 0,
  name: "",
  address: "",
  lat: "",
  long: "",
  primary: false,
};

const AddressManagement = ({
  initialData,
}: {
  initialData: iMerchant | null;
}) => {
  const { t } = useTranslation();
  const [addresses, setAddresses] = useState<iAddresses[]>([]);
  const [formValues, setFormValues] = useState<iAddresses>(INITIAL_FORM_VALUES);
  const [editMode, setEditMode] = useState<"add" | "edit" | null>(null);
  const [currentAddressId, setCurrentAddressId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>();
  const AddressSchema = yup.object().shape({
    addresses: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.number().required(),
          name: yup.string().required("Address name is required"),
          address: yup.string().optional(),
          lat: yup.string().required(),
          long: yup.string().required(),
          primary: yup.boolean().required(),
        })
      )
      .required("At least one address is required")
      .min(1, "At least one address is required"),
  });
  useEffect(() => {
    if (initialData?.addresses) {
      setAddresses(initialData.addresses);
    }
    const userId = localStorage.getItem(USER_ID);
    setUserId(userId!);
  }, [initialData]);

  const resetForm = useCallback(() => {
    setFormValues(INITIAL_FORM_VALUES);
    setEditMode(null);
    setCurrentAddressId(null);
  }, []);

  const handleAddressAction = async () => {
    try {
      setIsLoading(true);
      const requestData = {
        address: formValues.address,
        lat: formValues.lat,
        long: formValues.long,
        name: formValues.name,
        primary: formValues.primary,
      };

      if (editMode === "add") {
        setActionInProgress("Adding address...");
        const response = await addAddress(requestData, userId!);
        setAddresses((prev) => [
          ...prev,
          { ...response.data, primary: !!response.data.primary },
        ]);
        toast.success(t("addressManagement.addressAdded"));
      } else if (editMode === "edit" && currentAddressId) {
        setActionInProgress("Updating address...");
        const response = await updateAddress(
          { ...requestData, primary: formValues.primary ? 1 : 0 },
          currentAddressId.toString()
        );
        setAddresses((prev) =>
          prev.map((address) =>
            address.id === currentAddressId
              ? { ...response.data, primary: !!response.data.primary }
              : address
          )
        );
        toast.success(t("addressManagement.addressUpdated"));
      }
    } catch (error) {
      toast.error(
        editMode === "add"
          ? t("addressManagement.addError")
          : t("addressManagement.updateError")
      );
    } finally {
      setIsLoading(false);
      setActionInProgress(null);
      resetForm();
    }
  };

  const handleDeleteAddress = async (id: number) => {
    try {
      setActionInProgress(`deleting-${id}`);
      await deleteAddress(id.toString());
      setAddresses((prev) => prev.filter((address) => address.id !== id));
      toast.success(t("addressManagement.addressDeleted"));
    } catch (error) {
      toast.error(t("addressManagement.deleteError"));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleSetPrimary = async (data: iAddresses, id: number) => {
    try {
      setActionInProgress(`primary-${id}`);
      const requestData = { ...data, address: data.address || "", primary: 1 };
      await setAddressAsPrimary(requestData, id.toString());
      setAddresses((prev) =>
        prev.map((address) => ({
          ...address,
          primary: address.id === id ? true : false,
        }))
      );
      toast.success(t("addressManagement.primarySet"));
    } catch (error) {
      toast.error(t("addressManagement.primaryError"));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleLocationChange = (coordinates: Coordinates) => {
    setFormValues((prev) => ({
      ...prev,
      lat: coordinates.lat.toString(),
      long: coordinates.lng.toString(),
    }));
  };

  const handleEditClick = (address: iAddresses) => {
    setFormValues(address);
    setEditMode("edit");
    setCurrentAddressId(address.id);
  };

  const handleAddClick = () => {
    resetForm();
    setEditMode("add");
  };

  const getInitialCoordinates = (): Coordinates => {
    if (formValues.lat && formValues.long) {
      return {
        lat: parseFloat(formValues.lat),
        lng: parseFloat(formValues.long),
      };
    }
    return { lat: 31.9539, lng: 35.9106 }; // Default to Amman, Jordan coordinates
  };

  const renderAddressList = () =>
    addresses.length > 0 ? (
      [...addresses]
        .sort((a, b) => (b.primary ? 1 : 0) - (a.primary ? 1 : 0))
        .map((address) => (
          <div
            key={address.id}
            className="bg-gray-50 rounded-lg p-4 mb-4 shadow-sm border border-gray-100 relative"
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center space-x-2">
                <MapPin size={20} className="text-gray-500" />
                <h3 className="font-semibold text-gray-800">{address.name}</h3>
                {address.primary && (
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                    {t("addressManagement.primary")}
                  </span>
                )}
              </div>

              {!editMode && (
                <div className="flex items-center space-x-2">
                  <Edit
                    size={20}
                    className="text-gray-500 hover:text-blue-600 cursor-pointer"
                    onClick={() => handleEditClick(address)}
                  />
                  {actionInProgress === `deleting-${address.id}` ? (
                    <Loader size={20} className="animate-spin text-gray-500" />
                  ) : (
                    addresses.length > 1 &&
                    !address.primary && (
                      <Trash2
                        size={20}
                        className="text-gray-500 hover:text-red-600 cursor-pointer"
                        onClick={() => handleDeleteAddress(address.id)}
                      />
                    )
                  )}
                </div>
              )}
            </div>

            <div className="pl-8 space-y-1">
              <p className="text-gray-600 text-sm">{address.address}</p>

              {!address.primary && !editMode && (
                <Button
                  type="button"
                  disabled={actionInProgress === `primary-${address.id}`}
                  onClick={() => handleSetPrimary(address, address.id)}
                  className="mt-2 w-full md:w-auto text-sm text-blue-600 hover:bg-blue-50 border-none p-0"
                  variant="outlined"
                >
                  {actionInProgress === `primary-${address.id}` ? (
                    <div className="flex items-center justify-center">
                      <Loader size={16} className="animate-spin mr-2" />
                      {t("addressManagement.settingAsPrimary")}
                    </div>
                  ) : (
                    t("addressManagement.setAsPrimary")
                  )}
                </Button>
              )}
            </div>
          </div>
        ))
    ) : (
      <div className="py-4 text-center text-gray-500 border-t">
        {t("addressManagement.noAddresses")}
      </div>
    );

  return (
    <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
      <div className="flex flex-col md:flex-row justify-between items-center mb-4">
        <div className="text-lg font-medium mb-2 md:mb-0">
          {t("addressManagement.title")}
        </div>
        {!editMode && (
          <Button
            icon={<Plus size={16} />}
            onClick={handleAddClick}
            className="w-full md:w-auto"
          >
            {t("addressManagement.addAddress")}
          </Button>
        )}
      </div>

      {editMode && (
        <div className="py-4 border-t">
          <div className="flex flex-col md:flex-row justify-between items-center mb-4">
            <div className="font-medium mb-2 md:mb-0">
              {editMode === "edit"
                ? t("addressManagement.editAddress")
                : t("addressManagement.addNewAddress")}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("addressManagement.addressName")} *
              </label>
              <input
                value={formValues.name}
                onChange={(e) =>
                  setFormValues((prev) => ({ ...prev, name: e.target.value }))
                }
                className="border border-gray-300 rounded-md px-3 py-2 w-full"
                placeholder={t("addressManagement.addressNamePlaceholder")}
              />
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("addressManagement.additionalDetails")}
              </label>
              <textarea
                value={formValues.address}
                onChange={(e) =>
                  setFormValues((prev) => ({
                    ...prev,
                    address: e.target.value,
                  }))
                }
                className="border border-gray-300 rounded-md px-3 py-2 w-full"
                placeholder={t(
                  "addressManagement.additionalDetailsPlaceholder"
                )}
                rows={3}
              />
            </div>

            <MapLocationWidget
              initialCoordinates={getInitialCoordinates()}
              withSearch={true}
              onLocationChange={handleLocationChange}
              height="h-48 md:h-64"
              className="pb-4"
            />

            <div className="flex flex-col md:flex-row gap-2 justify-end">
              <Button
                icon={<X size={16} />}
                onClick={resetForm}
                className="w-full md:w-auto mb-2 md:mb-0"
              >
                {t("common.cancel")}
              </Button>
              <Button
                icon={<Save size={16} />}
                onClick={handleAddressAction}
                disabled={
                  !formValues.name || !formValues.lat || !formValues.long
                }
                isLoading={isLoading}
                className="w-full md:w-auto"
              >
                {isLoading
                  ? actionInProgress
                  : editMode === "edit"
                  ? t("common.update")
                  : t("common.save")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {renderAddressList()}
    </div>
  );
};

export default AddressManagement;
